import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { motion } from '../../../lib/motionShim';
import { Heart, ArrowRight, RotateCcw, Activity, Shield, Moon, Droplets, Brain, Flame, CheckCircle, Sparkles } from 'lucide-react';

const questions = [
    {
        id: 1,
        icon: Activity,
        category: 'Activity',
        question: 'How many days per week do you exercise?',
        options: [
            { text: '0–1 days', score: 1 },
            { text: '2–3 days', score: 2 },
            { text: '4–5 days', score: 3 },
            { text: '6–7 days', score: 4 },
        ],
    },
    {
        id: 2,
        icon: Moon,
        category: 'Sleep',
        question: 'How many hours of sleep do you get on average?',
        options: [
            { text: 'Less than 5 hours', score: 1 },
            { text: '5–6 hours', score: 2 },
            { text: '7–8 hours', score: 4 },
            { text: 'More than 8 hours', score: 3 },
        ],
    },
    {
        id: 3,
        icon: Droplets,
        category: 'Hydration',
        question: 'How much water do you drink daily?',
        options: [
            { text: 'Less than 1 litre', score: 1 },
            { text: '1–2 litres', score: 2 },
            { text: '2–3 litres', score: 3 },
            { text: '3+ litres', score: 4 },
        ],
    },
    {
        id: 4,
        icon: Flame,
        category: 'Nutrition',
        question: 'How would you describe your daily diet?',
        options: [
            { text: 'Mostly fast food & processed', score: 1 },
            { text: 'Mixed — some healthy, some not', score: 2 },
            { text: 'Mostly whole foods & balanced', score: 3 },
            { text: 'Carefully tracked & optimized', score: 4 },
        ],
    },
    {
        id: 5,
        icon: Brain,
        category: 'Stress',
        question: 'How would you rate your stress levels?',
        options: [
            { text: 'Constantly overwhelmed', score: 1 },
            { text: 'Stressed most days', score: 2 },
            { text: 'Manageable — occasional stress', score: 3 },
            { text: 'Calm and in control', score: 4 },
        ],
    },
    {
        id: 6,
        icon: Shield,
        category: 'Consistency',
        question: 'How consistent is your health routine?',
        options: [
            { text: 'I don\'t have a routine', score: 1 },
            { text: 'I try but fall off often', score: 2 },
            { text: 'Fairly consistent with slips', score: 3 },
            { text: 'Very disciplined & consistent', score: 4 },
        ],
    },
];

const getScoreData = (totalScore) => {
    const maxScore = questions.length * 4;
    const percentage = Math.round((totalScore / maxScore) * 100);

    let tier, tierColor, tierGradient, message, tip;

    if (percentage >= 85) {
        tier = 'Elite';
        tierColor = 'text-emerald-400';
        tierGradient = 'from-emerald-500 to-green-500';
        message = 'You\'re in the top tier. Your habits are dialed in — now it\'s about optimization and peak performance.';
        tip = 'STON.FIT\'s AI will fine-tune your routine to push past plateaus and keep you at the top.';
    } else if (percentage >= 65) {
        tier = 'Strong';
        tierColor = 'text-violet-400';
        tierGradient = 'from-violet-500 to-purple-500';
        message = 'You\'re doing well. A few targeted improvements could take you from good to exceptional.';
        tip = 'STON.FIT will identify exactly where to focus for maximum impact on your overall health score.';
    } else if (percentage >= 40) {
        tier = 'Building';
        tierColor = 'text-amber-400';
        tierGradient = 'from-amber-500 to-orange-500';
        message = 'You\'ve got a foundation — now it\'s time to build on it. Small, consistent changes will compound fast.';
        tip = 'STON.FIT creates a step-by-step plan tailored to your weak areas so progress feels effortless.';
    } else {
        tier = 'Starting';
        tierColor = 'text-rose-400';
        tierGradient = 'from-rose-500 to-pink-500';
        message = 'Every journey starts somewhere. The good news? You have the most potential for rapid improvement.';
        tip = 'STON.FIT will build your foundational habits first — sleep, nutrition, and movement — so every day gets better.';
    }

    return { percentage, tier, tierColor, tierGradient, message, tip };
};

const HealthScoreQuiz = () => {
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [result, setResult] = useState(null);

    const handleAnswer = (score) => {
        const newAnswers = [...answers, score];
        setAnswers(newAnswers);

        if (currentQuestion < questions.length - 1) {
            setCurrentQuestion(currentQuestion + 1);
        } else {
            const total = newAnswers.reduce((sum, s) => sum + s, 0);
            setResult(getScoreData(total));
        }
    };

    const resetQuiz = () => {
        setCurrentQuestion(0);
        setAnswers([]);
        setResult(null);
    };

    const scrollToWaitlist = () => {
        document.querySelector('#waitlist')?.scrollIntoView({ behavior: 'smooth' });
    };

    const currentQ = questions[currentQuestion];
    const CurrentIcon = currentQ?.icon;

    return (
        <div data-testid="health-score-quiz">
            <div className="max-w-[640px] mx-auto">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-pink-600 flex items-center justify-center">
                        <Heart className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h3 className="text-xl md:text-2xl font-bold text-ston-text">
                            Health Score
                        </h3>
                        <p className="text-sm text-ston-text-muted">6 questions · 30 seconds</p>
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="flex gap-1.5 mb-8">
                    {questions.map((_, index) => (
                        <motion.div
                            key={index}
                            className={`flex-1 h-1.5 rounded-full transition-all duration-500 ${index < currentQuestion || result
                                    ? 'bg-gradient-to-r from-fuchsia-500 to-pink-500'
                                    : index === currentQuestion && !result
                                        ? 'bg-ston-purple'
                                        : 'bg-ston-border'
                                }`}
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ delay: index * 0.05 }}
                        />
                    ))}
                </div>

                <AnimatePresence mode="wait">
                    {!result ? (
                        <motion.div
                            key={currentQuestion}
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -30 }}
                            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        >
                            {/* Question Counter + Category */}
                            <div className="flex items-center gap-2 mb-3">
                                <span className="text-xs font-mono text-ston-text-muted">
                                    {String(currentQuestion + 1).padStart(2, '0')}/{String(questions.length).padStart(2, '0')}
                                </span>
                                <span className="w-1 h-1 rounded-full bg-ston-text-muted" />
                                <div className="flex items-center gap-1.5">
                                    <CurrentIcon className="w-3.5 h-3.5 text-fuchsia-400" />
                                    <span className="text-xs font-medium text-fuchsia-400 uppercase tracking-wider">
                                        {currentQ.category}
                                    </span>
                                </div>
                            </div>

                            {/* Question */}
                            <h3
                                className="text-xl md:text-2xl font-bold text-ston-text mb-8 leading-snug"
                                data-testid={`quiz-question-${currentQuestion + 1}`}
                            >
                                {currentQ.question}
                            </h3>

                            {/* Options */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {currentQ.options.map((option, index) => (
                                    <motion.button
                                        key={index}
                                        onClick={() => handleAnswer(option.score)}
                                        className="group relative text-left p-4 rounded-xl bg-ston-surface/50 border border-ston-border hover:border-fuchsia-500/40 transition-all duration-200 overflow-hidden"
                                        whileHover={{ scale: 1.02, y: -2 }}
                                        whileTap={{ scale: 0.98 }}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.08 }}
                                        data-testid={`quiz-option-${index}`}
                                    >
                                        {/* Hover gradient */}
                                        <div className="absolute inset-0 bg-gradient-to-r from-fuchsia-500/0 to-pink-500/0 group-hover:from-fuchsia-500/5 group-hover:to-pink-500/5 transition-all duration-300 rounded-xl" />
                                        <span className="relative text-[15px] font-medium text-ston-text-secondary group-hover:text-ston-text transition-colors">
                                            {option.text}
                                        </span>
                                    </motion.button>
                                ))}
                            </div>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="result"
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                            className="text-center"
                        >
                            {/* Score Circle */}
                            <div className="relative w-52 h-52 mx-auto mb-6">
                                {/* Background circle */}
                                <svg className="w-full h-full -rotate-90">
                                    <circle
                                        cx="104"
                                        cy="104"
                                        r="92"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="8"
                                        className="text-ston-surface"
                                    />
                                    <motion.circle
                                        cx="104"
                                        cy="104"
                                        r="92"
                                        fill="none"
                                        stroke="url(#healthGradient)"
                                        strokeWidth="8"
                                        strokeLinecap="round"
                                        strokeDasharray={578}
                                        initial={{ strokeDashoffset: 578 }}
                                        animate={{ strokeDashoffset: 578 - (578 * result.percentage) / 100 }}
                                        transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
                                    />
                                    <defs>
                                        <linearGradient id="healthGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                            <stop offset="0%" stopColor="#d946ef" />
                                            <stop offset="100%" stopColor="#ec4899" />
                                        </linearGradient>
                                    </defs>
                                </svg>

                                {/* Center content */}
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <motion.div
                                        className="text-5xl font-black gradient-text"
                                        initial={{ opacity: 0, scale: 0.5 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: 0.5, type: "spring", bounce: 0.4 }}
                                        data-testid="health-score-value"
                                    >
                                        {result.percentage}
                                    </motion.div>
                                    <motion.div
                                        className="text-sm text-ston-text-muted"
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.7 }}
                                    >
                                        out of 100
                                    </motion.div>
                                </div>
                            </div>

                            {/* Tier Badge */}
                            <motion.div
                                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r ${result.tierGradient} mb-4`}
                                initial={{ scale: 0, rotate: -10 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ delay: 0.8, type: "spring" }}
                                data-testid="health-score-tier"
                            >
                                <Sparkles className="w-4 h-4 text-white" />
                                <span className="text-sm font-bold text-white tracking-wide">{result.tier}</span>
                            </motion.div>

                            {/* Message */}
                            <motion.p
                                className="mt-4 text-base text-ston-text-secondary leading-relaxed max-w-[440px] mx-auto"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 1 }}
                            >
                                {result.message}
                            </motion.p>

                            {/* Breakdown pills */}
                            <motion.div
                                className="mt-6 flex flex-wrap justify-center gap-2"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 1.1 }}
                            >
                                {questions.map((q, i) => {
                                    const score = answers[i];
                                    const QIcon = q.icon;
                                    return (
                                        <div
                                            key={i}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${score >= 3
                                                    ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                                                    : score >= 2
                                                        ? 'bg-amber-500/10 border-amber-500/20 text-amber-400'
                                                        : 'bg-rose-500/10 border-rose-500/20 text-rose-400'
                                                }`}
                                            data-testid={`quiz-trait-${i}`}
                                        >
                                            <QIcon className="w-3 h-3" />
                                            {q.category}
                                            {score >= 3 && <CheckCircle className="w-3 h-3" />}
                                        </div>
                                    );
                                })}
                            </motion.div>

                            {/* Tip */}
                            <motion.p
                                className="mt-5 text-sm text-ston-text-muted max-w-[400px] mx-auto"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 1.2 }}
                            >
                                {result.tip}
                            </motion.p>

                            {/* CTAs */}
                            <motion.div
                                className="mt-8 flex flex-col items-center gap-4"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 1.3 }}
                            >
                                <motion.button
                                    onClick={scrollToWaitlist}
                                    className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-fuchsia-500 to-pink-600 text-white rounded-full text-base font-semibold hover:shadow-lg hover:shadow-fuchsia-500/30 transition-all"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    data-testid="quiz-cta-btn"
                                >
                                    Improve My Score
                                    <ArrowRight size={18} />
                                </motion.button>

                                <button
                                    onClick={resetQuiz}
                                    className="inline-flex items-center gap-2 text-[13px] text-ston-text-muted hover:text-ston-text-secondary underline transition-colors"
                                    data-testid="quiz-retake-btn"
                                >
                                    <RotateCcw size={14} />
                                    Retake Quiz
                                </button>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default HealthScoreQuiz;
